import {
  skyelements,
  elements,
  covid,
  clockhours,
  fruits,
  organs,
  drinks,
  horoscope,
  vegetables,
  field,
  waterAnimals,
  roughAnimals,
  handsigns,
  invert,
  insects,
  reptiles,
  rodents,
  cuteAnimals,
  hugeAnimals,
  clothing,
  party,
  sweet,
  summer,
  holidays,
  winter,
  colorful,
  professions,
  activities,
  cloudymood,
  filmset,
  pokerset,
  randomhats,
  shoes,
  colorfullbooks,
  dayNight,
  divingEquipment,
} from './sets'

// Niveles para HitMoji (Match-3)
export const hitLevels = [
  skyelements,   // Level 1 - Fácil
  elements,      // Level 2
  covid,         // Level 3
  clockhours,    // Level 4
  fruits,        // Level 5
  organs,        // Level 6
  drinks,        // Level 7
  horoscope,     // Level 8
  vegetables,    // Level 9
  field,         // Level 10
  waterAnimals,  // Level 11
  roughAnimals,  // Level 12
  handsigns,     // Level 13 - Difícil
]

// Niveles para FindMoji
export const findLevels = [
  invert,
  insects,
  reptiles,
  rodents,
  cuteAnimals,
  roughAnimals,
  waterAnimals,
  hugeAnimals,
  clothing,
  party,
  sweet,
  summer,
  holidays,
  winter,
  colorful,
  professions,
  activities,
]

// Niveles para Memoji
export const memojiLevels = [
  divingEquipment, // 0 - Equipo de buceo (Open Water)
  cloudymood,      // 1
  insects,        // 5 pairs
  filmset,        // 5 pairs
  pokerset,       // 5 pairs
  randomhats,     // 6 pairs
  shoes,          // 6 pairs
  colorfullbooks, // 6 pairs
  cuteAnimals,    // 8 pairs
  handsigns,      // 10 pairs
  horoscope,      // 8 pairs
  dayNight,       // 8 pairs
  clockhours,     // 6 pairs
  roughAnimals,   // 10 pairs
  hugeAnimals,    // 10 pairs
  waterAnimals,   // 10 pairs
]

// Helper para obtener el set de un nivel
export const getLevelSet = (game: 'hit' | 'find' | 'memoji', level: number): string[] => {
  const levels = game === 'hit' ? hitLevels : game === 'find' ? findLevels : memojiLevels
  return levels[Math.min(level, levels.length - 1)] || levels[0]
}

// Info de cada nivel
export interface LevelInfo {
  level: number
  set: string[]
  name: string
  emoji: string
}

export const getHitLevelInfo = (level: number): LevelInfo => {
  const names = [
    'Cielo', 'Elementos', 'Pandemia', 'Tiempo', 'Frutas',
    'Cuerpo', 'Bebidas', 'Zodiaco', 'Verduras', 'Campo',
    'Mar', 'Salvajes', 'Manos'
  ]
  const emojis = ['💫', '🌿', '😷', '🕐', '🍑', '🧠', '🍺', '♈️', '🥦', '🌾', '🐙', '🦁', '👌']
  
  return {
    level,
    set: hitLevels[level] || hitLevels[0],
    name: names[level] || `Nivel ${level + 1}`,
    emoji: emojis[level] || '⭐️'
  }
}

// =============================================
// EVOJI - Secuencias Evolutivas (2048 con emojis)
// =============================================

// Cada array representa una secuencia de evolución (de menor a mayor)
// El jugador combina 2 emojis iguales para obtener el siguiente nivel

export interface EvojiLevel {
  name: string
  emoji: string  // Emoji representativo del nivel
  sequence: string[]  // Secuencia evolutiva (11 elementos para llegar al "2048")
  description: string
}

export const evojiLevels: EvojiLevel[] = [
  {
    name: 'Hogares',
    emoji: '🏠',
    sequence: ['🔥', '🕳️', '⛺', '🛖', '🏠', '🏡', '🏘️', '🏰', '🏯', '🏙️', '🗼'],
    description: 'Del fuego al rascacielos'
  },
  {
    name: 'Transporte',
    emoji: '🚀',
    sequence: ['🦶', '🛹', '🚲', '🏍️', '🚗', '🚌', '🚂', '✈️', '🚀', '🛸', '⭐'],
    description: 'De caminar al espacio'
  },
  {
    name: 'Evolución',
    emoji: '🧬',
    sequence: ['🦠', '🐟', '🦎', '🐒', '🧑', '👷', '🧑‍🔬', '🧑‍🚀', '🧙', '👽', '🌌'],
    description: 'Del microbio al alien'
  },
  {
    name: 'Comida',
    emoji: '🍕',
    sequence: ['🥚', '🍳', '🥗', '🍔', '🍕', '🌮', '🍱', '🍜', '🦞', '👨‍🍳', '👑'],
    description: 'Del huevo al chef real'
  },
  {
    name: 'Naturaleza',
    emoji: '🌳',
    sequence: ['🌱', '🌿', '🌾', '🌻', '🌳', '🌴', '🎋', '🎄', '🗿', '🏔️', '🌋'],
    description: 'De la semilla al volcán'
  },
  {
    name: 'Riqueza',
    emoji: '💎',
    sequence: ['🪙', '💵', '💰', '💎', '👑', '🏰', '🏝️', '🌍', '⭐', '🌌', '✨'],
    description: 'De la moneda al universo'
  },
  {
    name: 'Océano',
    emoji: '🐋',
    sequence: ['🦠', '🦐', '🐟', '🐠', '🐡', '🦑', '🐙', '🦈', '🐋', '🔱', '🧜'],
    description: 'Del plancton al rey del mar'
  },
  {
    name: 'Tecnología',
    emoji: '🤖',
    sequence: ['🪨', '🔧', '⚙️', '📞', '💻', '📱', '🤖', '🧠', '🔮', '✨', '🌟'],
    description: 'De la piedra a la IA'
  },
  {
    name: 'Espacio',
    emoji: '🌍',
    sequence: ['🌑', '🌒', '🌓', '🌔', '🌕', '🌍', '🪐', '⭐', '💫', '🌌', '🕳️'],
    description: 'De la luna al agujero negro'
  },
  {
    name: 'Amor',
    emoji: '❤️',
    sequence: ['👀', '😊', '😍', '💕', '💗', '💖', '💝', '❤️‍🔥', '💑', '👨‍👩‍👧', '🏠'],
    description: 'De una mirada a un hogar'
  },
  {
    name: 'Magia',
    emoji: '🧙',
    sequence: ['📖', '🕯️', '🔮', '⚗️', '🧪', '✨', '💫', '⭐', '🌟', '🧙', '🐉'],
    description: 'Del libro al dragón'
  },
  {
    name: 'Guerrero',
    emoji: '⚔️',
    sequence: ['👶', '🧒', '🧑', '🏃', '🤺', '🗡️', '⚔️', '🛡️', '🦸', '👑', '🐉'],
    description: 'De niño a leyenda'
  },
]

// Helper para obtener el valor numérico equivalente (como en 2048)
export const getEvojiValue = (emojiIndex: number): number => {
  return Math.pow(2, emojiIndex + 1) // 2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048
}
